/**
 * Ein einzelnes Gerät.
 *
 * Die Reihenfolge ist die der Fragen, mit denen jemand hierherkommt (HM-073,
 * `ui_kits/dashboard/DeviceDetail.jsx`): **Hakt gerade etwas?** — die gelben
 * Hinweise über allem. **Welche Ansicht?** — zwei Reiter samt
 * Erreichbarkeitsmarke. Und dann **Übersicht** (wie geht es ihm, was kommt an)
 * und **Einstellungen** (was ist einzurichten, was sagt es über sich, wie
 * verlässt es die Anlage).
 *
 * **Seit HM-113 hat die Seite so viele Gestalten, wie es Geräteklassen gibt.**
 * Bis dahin war sie die Seite des Lesekopfs, und jedes andere Gerät bekam sie
 * mit: das Gateway ein Formular mit Update-Kanal und elf Handlungen, von denen
 * es keine annimmt; die Wallbox hinter dem Gateway „Neu starten", „Update
 * installieren" und „Gerät freigeben" — dreimal `409`. Welche Klasse ein Gerät
 * hat und was daraus folgt, rechnet `lib/device-profile.ts`:
 *
 * | Klasse           | Übersicht                         | Einstellungen                                       |
 * | ---------------- | --------------------------------- | --------------------------------------------------- |
 * | eigene Verbindung | Kacheln, Datenpunkte             | Name, Konfiguration, Auskünfte, Aktionen, Freigeben |
 * | Gateway          | Kacheln, **Geräte dahinter**      | Name, gemeldete Einstellungen, Auskünfte, Freigeben |
 * | hinter Gateway   | Kacheln samt Gateway, Datenpunkte | Name, Zuordnung, **Am Gateway**, Aufträge, **Ablehnen** |
 *
 * Was ein einzelnes Erzeugnis darüber hinaus kann, hängt am **Typ** — heute der
 * Ladepunkt (`evcc_loadpoint`, HM-110): Er bekommt seit HM-119 einen dritten
 * Reiter **„Steuerung"** zwischen den beiden, mit derselben Bedienung wie die
 * Seite „Laden" und den Begriffen von evcc.
 *
 * **Der gewählte Reiter steht in der Adresse** (`lib/router.ts`), nicht in
 * einem `useState` wie im Entwurf. Sonst wäre die Konfiguration eines Geräts
 * nicht verlinkbar, ein Neuladen nach dem Absenden landete auf der Übersicht,
 * und die Zurück-Taste ginge an der Hälfte der Seite vorbei (HM-037).
 *
 * **Gezeigt wird ausschließlich, was wirklich geliefert wurde.** Keine Kachel
 * mit „—", keine Zeile mit Platzhalter, keine Karte ohne Inhalt. An einem
 * frisch gekoppelten Gerät bleibt deshalb eine einzige Kachel übrig („noch
 * keine Daten") und unter den Auskünften **ein** Satz, was fehlt.
 *
 * **„Neu verbinden" aus dem Kopf des Kits gibt es nicht.** Der Endpunkt
 * `POST /devices/{id}/credentials` steht — aber er gibt ein Geheimnis aus, das
 * genau einmal in der Antwort steht und das niemand dem Gerät mitteilen kann.
 * Der Weg nach einem Werksreset ist das **erneute Koppeln** (HM-035); er steht
 * unter „Einstellungen" über dem roten Kasten.
 *
 * **Der rote Kasten heißt „Gerät freigeben", nicht „Gerät entfernen".**
 * Freigeben widerruft die Zugangsdaten; die Rohmesswerte laufen mit ihrer Frist
 * aus und werden nicht sofort gelöscht (OQ-073) — die **Zählerhistorie bleibt**, denn der Verbrauch gehört dem Zähler (HM-034,
 * ADR 0018). Ein Gerät hinter dem Gateway hat keine Zugangsdaten; es wird **am
 * Gateway abgelehnt**, und die API weist ein Freigeben mit `409` ab.
 */
import { RAW_RETENTION_DAYS } from "../lib/legal.js";
import { useState, type ReactNode } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Alert, Badge, Button, Card, Dialog, EmptyState, Input, icons } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { api } from "../lib/client.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { formatMonthYear, formatTimeAgo } from "../lib/format.js";
import { formatDatapointValue } from "../lib/datapoint-format.js";
import { CLAIM_STATUS_LABELS, DATAPOINT_ACCESS_LABELS } from "../lib/device-labels.js";
import {
  isEvccLoadpointDevice,
  isEvccSiteDevice,
  isInverterDevice,
  isStorageDevice,
} from "../lib/device-type.js";
import { EvccSiteSection } from "../components/evcc-site-section.js";
import { deviceClass, deviceSurface, type DeviceClass } from "../lib/device-profile.js";
import {
  deviceHints,
  hardwareRows,
  nameSource,
  meterRows,
  networkRows,
  nodeRows,
  reachabilityBadge,
  statTiles,
  systemRows,
  type Row,
  type Tile,
} from "../lib/device-report.js";
import { navigate, routePath, type DeviceTab, type Route } from "../lib/router.js";
import {
  DeviceActionsSection,
  DeviceCommandHistory,
  DeviceConfigSection,
} from "../components/device-control.js";
import { DefinitionList } from "../components/definition-list.js";
import { HintList } from "../components/hint-list.js";
import { Section, SectionHeading } from "../components/section.js";
import { ChargePointControl } from "../components/charge-point.js";
import { GatewayNodeList } from "../components/gateway-section.js";
import { canWriteDatapoint, DatapointWriteButton } from "../components/datapoint-write.js";
import { formatKw, formatKwh, storageRows } from "../lib/generation-asset-device.js";
import {
  useChargePoint,
  useDatapoints,
  useDatapointValues,
  useDevice,
  useGatewayNodes,
  useIntakeStatus,
  useIntegrations,
  useInverterState,
  useStorageState,
} from "../lib/queries.js";
import {
  groupDatapoints,
  type DatapointEntry,
  type DatapointGroup,
} from "../lib/datapoint-groups.js";
import { useSiteAccess } from "../lib/site-access.js";

type Device = ApiComponents["schemas"]["Device"];
type GatewayNode = ApiComponents["schemas"]["GatewayNode"];

export function DeviceDetailScreen({
  siteId,
  siteName,
  deviceId,
  tab,
  onBack,
}: {
  siteId: string;
  siteName: string;
  deviceId: string;
  /** Welcher Reiter offen ist — er steht in der Adresse, nicht in dieser Seite. */
  tab: DeviceTab;
  /** Zurück in die Geräteverwaltung — auch nach dem Freigeben. */
  onBack: () => void;
}) {
  const device = useDevice(deviceId);
  /**
   * **Ohne diese Auskunft erklärt die Seite den häufigsten Fall falsch.** Steht
   * die Annahme still, altert `lastSeenAt` an jedem Gerät gleichzeitig, und die
   * Marke springt auf „überfällig" — hier stünde dann ein Hinweis, der zu einem
   * Neustart des Geräts rät, an dem nichts kaputt ist.
   */
  const intake = useIntakeStatus();
  /**
   * **Das Gateway eines Geräts dahinter** (HM-113). Schweigt es, schweigt das
   * Gerät mit — und ohne diese Abfrage stünde hier der Rat, das Gerät zu
   * prüfen, an dem nichts kaputt ist. Derselbe Abfrageschlüssel wie auf der
   * Seite des Gateways: kein zweiter Abruf, wenn man von dort kommt.
   */
  const gateway = useDevice(device.data?.gatewayDeviceId ?? null);
  const integrations = useIntegrations(siteId);
  const [error, setError] = useState<string | null>(null);

  /** Den Reiter wechseln — über die Adresse, damit die Zurück-Taste ihn kennt. */
  const go = (target: DeviceTab): void => navigate(deviceRoute(siteId, deviceId, target));

  const sourceName =
    device.data?.integrationInstanceId === null || device.data === undefined
      ? null
      : (integrations.data?.items.find((item) => item.id === device.data.integrationInstanceId)
          ?.name ?? null);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 1100 }}>
      {/**
       * **Kein Kopf auf der Seite** (HM-073). Name, „Hersteller Erzeugnis ·
       * Seriennummer" und der Weg zurück stehen in der Kopfleiste des Rahmens —
       * so legt es der Entwurf an, und so macht es seit HM-072 der Zähler.
       *
       * Mit der Kopfkarte sind drei Dinge umgezogen: Die **Erreichbarkeitsmarke**
       * steht neben den Reitern (nur, wenn sie von „verbunden" abweicht), der
       * **Name** wird unter „Einstellungen" geändert, und der **Diagnose-Knopf**
       * ist ersatzlos weg — er stand dort, weil die Aktionen drei Reiter
       * entfernt lagen; sie liegen jetzt einen Klick weiter, und der Hinweis
       * oben führt genau dorthin.
       */}

      {device.isPending && <Card padding="sm">Wird geladen …</Card>}

      {/**
       * Ein Gerät, das es nicht gibt, ist ein **Zustand** und kein Ladefehler:
       * Die Adresse kann von gestern sein oder das Gerät ist inzwischen
       * freigegeben. Deshalb ein erklärter Leerzustand mit dem Weg zurück und
       * keine rote Fehlermeldung.
       */}
      {device.isError && (
        <Card>
          <EmptyState
            title="Dieses Gerät gibt es nicht"
            icon={<icons.IconDevice size={30} />}
            size="md"
            action={
              <Button variant="secondary" onClick={onBack}>
                Zur Geräteverwaltung
              </Button>
            }
          >
            Es wurde freigegeben, oder die Adresse stimmt nicht mehr. In der Geräteverwaltung steht,
            was diese Anlage führt.
          </EmptyState>
        </Card>
      )}

      {/**
       * Ein Gerät gehört genau einer Anlage (ADR 0017). Steht in der Adresse
       * eine andere, wird **nicht stillschweigend umgeschaltet**: Die Kopfzeile
       * der Anwendung nennt weiter die gewählte Anlage, und eine Seite, die
       * dazu das Gerät einer anderen zeigte, behauptete einen Zusammenhang, den
       * es nicht gibt (Regel 9).
       */}
      {device.data !== undefined && device.data.siteId !== siteId && (
        <Card>
          <EmptyState
            title="Dieses Gerät gehört zu einer anderen Anlage"
            icon={<icons.IconDevice size={30} />}
            size="md"
            action={
              <Button variant="secondary" onClick={onBack}>
                Zur Geräteverwaltung
              </Button>
            }
          >
            Wechsle oben die Anlage, um es zu sehen. Ein Gerät steht immer bei der Anlage, der
            es zugeordnet ist.
          </EmptyState>
        </Card>
      )}

      {device.data !== undefined && device.data.siteId === siteId && (
        <>
          {/**
           * **Die Hinweise stehen ganz oben, über den Reitern** — wie im
           * Entwurf und aus demselben Grund wie am Zähler: Was hakt, gilt dem
           * Gerät und nicht einer seiner beiden Ansichten. Unter „Übersicht"
           * verschwände der Hinweis beim Umschalten, und wer gerade
           * nachsieht, warum nichts ankommt, ist genau derjenige, den er
           * angeht.
           */}
          <HintList
            hints={deviceHints(device.data, undefined, intake.data, gateway.data)}
            onGo={go}
          />

          {/* Ansicht und Zustand in einer Zeile — wie am Zähler (HM-071). */}
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "var(--sp-3)",
            }}
          >
            <TabStrip
              siteId={siteId}
              deviceId={deviceId}
              current={effectiveTab(device.data, tab)}
              control={isEvccLoadpointDevice(device.data.deviceType)}
            />
            <ReachabilityBadge device={device.data} />
          </div>

          {/* Der Fehler stand in der Kopfkarte, weil beide Knöpfe darin ihn
              auslösten. Ausgelöst wird er jetzt aus den Einstellungen — dort
              steht er auch, direkt an der Handlung. */}
          {error !== null && <Alert tone="danger">{error}</Alert>}

          <Panel
            device={device.data}
            gateway={gateway.data}
            tab={effectiveTab(device.data, tab)}
            siteId={siteId}
            siteName={siteName}
            sourceName={sourceName}
            onBack={onBack}
            onError={setError}
            onGo={go}
          />
        </>
      )}
    </div>
  );
}

/**
 * **„Steuerung" gibt es nur am Ladepunkt.** Eine Adresse `…/steuerung` an einem
 * Lesekopf ist ein alter oder falsch kopierter Link; sie zeigt die Übersicht,
 * statt einen leeren Reiter zu öffnen.
 */
function effectiveTab(device: Device, tab: DeviceTab): DeviceTab {
  return tab === "control" && !isEvccLoadpointDevice(device.deviceType) ? "overview" : tab;
}

/** Die Adresse eines Reiters — einmal gebildet, für `href` und für `navigate`. */
function deviceRoute(siteId: string, deviceId: string, tab: DeviceTab): Route {
  return { kind: "device", siteId, deviceId, tab };
}

/**
 * Was unter den Reitern steht — **je Geräteklasse** (HM-113).
 *
 * **Zwei Fälle und kein Rückfall.** Ein unbekannter Reiter kommt hier nicht an:
 * `parseRoute` deutet nur die Slugs und schickt alles andere auf `start`.
 */
function Panel({
  device,
  gateway,
  tab,
  siteId,
  siteName,
  sourceName,
  onBack,
  onError,
  onGo,
}: {
  device: Device;
  /** Das Gateway eines Geräts dahinter; `undefined`, solange es lädt oder keines gibt. */
  gateway: Device | undefined;
  tab: DeviceTab;
  siteId: string;
  siteName: string;
  sourceName: string | null;
  onBack: () => void;
  onError: (message: string | null) => void;
  onGo: (tab: DeviceTab) => void;
}) {
  const kind = deviceClass(device);
  /* Derselbe Abfrageschlüssel wie die Liste darunter — kein zweiter Abruf. */
  const datapoints = useDatapoints(kind === "gateway" ? null : device.id);
  const surface = deviceSurface(device, {
    hasWritableDatapoint: (datapoints.data?.items ?? []).some(canWriteDatapoint),
  });
  /**
   * Die Zeile des Knotens beim Gateway: was das Gateway über das Gerät weiß,
   * und die Kennung, unter der es abgelehnt wird. Nur für ein Gerät dahinter.
   */
  const nodes = useGatewayNodes(kind === "gateway_node" ? device.gatewayDeviceId : null);
  const node = nodes.data?.items.find((item) => item.deviceId === device.id);
  // Was die Teilung nicht erlaubt, fehlt (HM-160, Regel 10): Aufträge sind
  // Schreiben, Freigeben und Ablehnen bleiben dem Besitzer.
  const rights = useSiteAccess();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--sp-5)" }}>
      {/**
       * **Übersicht heißt: Was kommt an?** (HM-073.) Wer ein Gerät öffnet, will
       * wissen, ob es sich meldet und was ankommt. An einem Lesekopf sind das
       * seine Datenpunkte; an einem Gateway die **Geräte dahinter** — es misst
       * nichts selbst, und eine leere Datenpunktliste mit dem Satz „sobald es
       * sich verbindet, trägt es seinen Katalog ein" war dort schlicht falsch.
       */}
      {tab === "overview" && (
        <>
          {/* Die Kacheln stehen im Reiter, nicht darüber: Sie sagen, wie es dem
              Gerät geht — eine Frage der Übersicht. */}
          <TileRow tiles={statTiles(device, undefined, gateway)} />
          {surface.nodes && (
            <Section
              title="Geräte hinter diesem Gateway"
              subtitle="Was das Gateway in deinem Netz gefunden hat. Werte kommen erst von einem Gerät, das du zugelassen hast."
            >
              <Card padding="none">
                <GatewayNodeList gatewayId={device.id} siteId={siteId} />
              </Card>
            </Section>
          )}
          {/* **Der Speicher** (HM-122): Ladezustand und Leistung, gedeutet in der
              API — vor den Datenpunkten, denn das ist die Frage, mit der man
              einen Speicher öffnet. */}
          {isStorageDevice(device.deviceType) && <StorageSection deviceId={device.id} />}
          {/* **Der Wechselrichter** (HM-126): PV-Eingang, Batterie und Netzseite,
              soweit er sie meldet — dieselbe Stelle, dieselbe Begründung. */}
          {isInverterDevice(device.deviceType) && <InverterSection deviceId={device.id} />}
          {/* **Der evcc-Standort** (HM-165): Tarife und Hausakku-Regeln — gelesen
              und, für wen schreiben darf, eingestellt. */}
          {isEvccSiteDevice(device.deviceType) && (
            <EvccSiteSection deviceId={device.id} canWrite={rights.canWrite} />
          )}
          {surface.datapoints && <DatapointsSection device={device} siteId={siteId} />}
        </>
      )}

      {/**
       * **Steuerung heißt: Wie soll er laden?** (HM-119.) Bis dahin stand der
       * Wallbox-Abschnitt in der Übersicht über den Datenpunkten — eine
       * Bedienfläche zwischen Kacheln und Katalog, die man beim Ablesen jedes
       * Mal überscrollte.
       */}
      {tab === "control" && <DeviceChargePointControl deviceId={device.id} />}

      {/**
       * **Einstellungen heißt: Was ist einmal einzurichten?** In dieser
       * Reihenfolge: was sich von hier aus ändern lässt, was das Gerät über sich
       * sagt, was man auslösen kann, was zuletzt geschickt wurde — und zuletzt,
       * wie es die Anlage verlässt. Was ein Gerät nicht annimmt, steht nicht da
       * (Regel 10), und dann entfällt die Karte, nicht nur ihr Knopf.
       */}
      {tab === "settings" && (
        <>
          <NameSection device={device} onError={onError} />
          {kind !== "gateway_node" && (
            <DeviceConfigSection device={device} onShowCommands={() => onGo("settings")} />
          )}
          <ReportSections
            device={device}
            kind={kind}
            siteName={siteName}
            sourceName={sourceName}
            gateway={gateway}
            node={node}
          />
          {kind !== "gateway_node" && rights.canWrite && (
            <DeviceActionsSection device={device} />
          )}
          <DeviceCommandHistory device={device} hideWhenEmpty={!surface.sendsCommands} />
          {surface.leave === null || !rights.canDelete ? null : surface.leave === "release" ? (
            <ReleaseSection device={device} kind={kind} onReleased={onBack} onError={onError} />
          ) : (
            <DismissNodeSection
              device={device}
              gateway={gateway}
              node={node}
              nodesLoaded={!nodes.isPending}
              siteId={siteId}
              onDismissed={onBack}
              onError={onError}
            />
          )}
        </>
      )}
    </div>
  );
}

/**
 * Die Erreichbarkeitsmarke neben den Reitern.
 *
 * **Nur, wenn sie von der Regel abweicht** (Entwurf): „Verbunden" ist der
 * Normalfall und braucht kein Abzeichen — dieselbe Regel wie am Zähler, wo
 * „Aktiv" keines bekommt. Sie stand bis HM-073 in der Kopfkarte neben dem
 * Namen; die gibt es nicht mehr, und die Reiterleiste ist die einzige Zeile
 * dieser Seite, die zu beiden Ansichten gehört.
 *
 * Gerechnet wird sie nicht hier: `device.reachability` kommt fertig aus der API
 * (`deviceReachability` in `@hausmeter/domain`), samt der einen Ausnahme für ein
 * Gerät, das der Cloud nie etwas schickt.
 */
function ReachabilityBadge({ device }: { device: Device }) {
  const badge = reachabilityBadge(device);
  /* **„Erreichbar" ist der Normalfall und bekommt keines** — die Ausnahme ist
     ein Gerät im reinen Lokalbetrieb: Dort sagt „nur lokal" etwas, das man
     sonst nirgends erführe, denn es *ist* nicht erreichbar und das ist keine
     Störung (HM-042). */
  if (device.reachability === "reachable" && device.operatingMode !== "local_only") return null;

  return (
    <Badge tone={badge.tone} title={badge.title}>
      {badge.label}
    </Badge>
  );
}

/**
 * Wie der Name dieses Geräts zu ändern ist — oder warum nicht hier.
 *
 * **Was von der Kopfkarte übrig geblieben ist** (HM-073). Sie trug Umbenennen,
 * den Hinweis auf die Konfiguration und den auf die Geräteoberfläche; alles
 * drei gehört zur Einrichtung und steht deshalb im zweiten Reiter.
 *
 * **Drei Fälle, und nur zwei erscheinen** (`nameSource`, geprüft in
 * `lib/device-report.ts`):
 *
 * - `user` — das Gerät meldet keinen Namen, hier ist die einzige Quelle: Knopf.
 * - `device_local` — es meldet einen und nimmt keinen entgegen: ein Satz, der
 *   nach Hause verweist.
 * - `device_remote` — es meldet einen **und** nimmt einen entgegen: **nichts**.
 *   Das Feld „Gerätename" steht eine Karte tiefer in der Konfiguration, und der
 *   Knopf „Zur Konfiguration" führte von hier aus auf sich selbst. Er ist
 *   weggefallen, weil sein Grund weggefallen ist — dieselbe Bewegung wie beim
 *   Diagnose-Knopf.
 */
function NameSection({
  device,
  onError,
}: {
  device: Device;
  onError: (message: string | null) => void;
}) {
  const [renaming, setRenaming] = useState(false);
  const source = nameSource(device);
  const rights = useSiteAccess();

  if (source === "device_remote") return null;

  return (
    <>
      <Section
        title="Name"
        subtitle={
          source === "user"
            ? "Dieses Gerät meldet keinen eigenen Namen — hier ist die einzige Quelle."
            : "Das Gerät meldet seinen Namen selbst."
        }
        action={
          source === "user" && rights.canWrite ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                onError(null);
                setRenaming(true);
              }}
            >
              Umbenennen
            </Button>
          ) : undefined
        }
      >
        <Card>
          <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
            {source === "user" ? (
              <>
                Der Name steht in der Kopfzeile und in der Geräteliste. Meldet das Gerät später
                einen eigenen, <strong>gewinnt dieser</strong> — ein Gerät, das an seiner eigenen
                Oberfläche anders heißt, sieht hier sonst aus wie ein zweites.
              </>
            ) : (
              <>
                Ändern lässt er sich am Gerät selbst; er gilt dann auch hier. Fernsetzen geht bei
                diesem Gerät nicht — es führt{" "}
                <span style={{ fontFamily: "var(--font-mono)" }}>deviceName</span> nicht unter dem,
                was es entgegennimmt.
              </>
            )}
          </p>
        </Card>
      </Section>

      <RenameDialog
        device={device}
        open={renaming}
        onClose={() => setRenaming(false)}
        onError={onError}
      />
    </>
  );
}

/**
 * Die vier Kacheln unter der Kopfkarte — der Zustand auf einen Blick.
 *
 * **Eine Kachel, die nichts zu sagen hat, entsteht gar nicht** (`statTiles` in
 * `lib/device-report.ts`); bleibt keine übrig, fehlt die Reihe. Das Raster ist
 * `auto-fit`, sodass die verbliebenen die Breite füllen, ohne dass hier etwas
 * nachgezogen werden müsste.
 *
 * `tone` färbt eine Kachel gelb, wenn an ihr etwas hakt. Das ist **keine
 * Größenfarbe** (ADR 0013): Sie sagt „daran fehlt etwas", nicht „das ist viel".
 */
function TileRow({ tiles }: { tiles: readonly Tile[] }) {
  if (tiles.length === 0) return null;

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))",
        gap: "var(--sp-3)",
      }}
    >
      {tiles.map((tile) => (
        <div
          key={tile.label}
          style={{
            padding: "12px 14px",
            borderRadius: "var(--radius-lg)",
            background: tile.tone === "warn" ? "var(--tone-warning-bg)" : "var(--surface-inset)",
            border: `1px solid ${tile.tone === "warn" ? "var(--tone-warning-border)" : "var(--border-subtle)"}`,
          }}
        >
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 600,
              letterSpacing: "var(--tracking-caps)",
              textTransform: "uppercase",
              color: "var(--text-faint)",
            }}
          >
            {tile.label}
          </div>
          <div
            style={{
              marginTop: 4,
              fontSize: 17,
              fontWeight: 700,
              fontFamily: "var(--font-mono)",
              color: tile.tone === "warn" ? "var(--hm-warning)" : "var(--text-strong)",
            }}
          >
            {tile.value}
          </div>
          {tile.note !== undefined && (
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{tile.note}</div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * Die Reiterleiste.
 *
 * **Echte Links und keine Knöpfe.** Jeder Reiter hat seit dieser Fassung eine
 * Adresse; ein `<button>` davor wäre derselbe Fehler wie die Gerätezeile im Kit,
 * die als `<div onClick>` gebaut ist — nicht im neuen Tab zu öffnen, nicht zu
 * kopieren, für eine Sprachausgabe kein Ziel. Strg-, Cmd- und Mittelklick gehen
 * deshalb an den Browser weiter.
 *
 * `aria-current="page"` statt `role="tab"`: Was hier steht, sind Verweise auf
 * vier Adressen und keine Reiter eines Widgets — eine Sprachausgabe soll sie als
 * Navigation lesen und nicht als Formularbedienung.
 */
function TabStrip({
  siteId,
  deviceId,
  current,
  control,
}: {
  siteId: string;
  deviceId: string;
  current: DeviceTab;
  /** Ob das Gerät ein Ladepunkt ist und den Reiter „Steuerung" trägt (HM-119). */
  control: boolean;
}) {
  /**
   * **Zwei statt vier** (HM-073) — dieselbe Teilung wie am Zähler: Ablesen und
   * Einrichten. „Datenpunkte", „Konfiguration" und „Aktionen" waren drei
   * Antworten auf zwei Fragen; wer wissen wollte, ob ein Gerät liefert, klickte
   * in den zweiten Reiter, und die Übersicht zeigte ihm stattdessen die
   * MAC-Adresse.
   *
   * **Die Zahl am Reiter steht nicht da.** Sie wäre die Kanalzahl und käme aus
   * der Gerätezeile; die Datenpunkte hängen an einer eigenen Abfrage, und sie
   * hier nur für eine Ziffer zu laden hieße, jeden Seitenaufruf um einen Aufruf
   * teurer zu machen.
   */
  const tabs: readonly { tab: DeviceTab; label: string }[] = [
    { tab: "overview", label: "Übersicht" },
    ...(control ? [{ tab: "control" as const, label: "Steuerung" }] : []),
    { tab: "settings", label: "Einstellungen" },
  ];

  return (
    <nav
      aria-label="Bereiche dieses Geräts"
      style={{
        display: "flex",
        gap: 4,
        padding: 4,
        alignSelf: "flex-start",
        flexWrap: "wrap",
        background: "var(--surface-sunken)",
        borderRadius: "var(--radius-md)",
      }}
    >
      {tabs.map(({ tab, label }) => {
        const route = deviceRoute(siteId, deviceId, tab);
        const active = tab === current;

        return (
          <a
            key={tab}
            href={routePath(route)}
            aria-current={active ? "page" : undefined}
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              navigate(route);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "7px 14px",
              borderRadius: "var(--radius-sm)",
              textDecoration: "none",
              fontFamily: "var(--font-body)",
              fontSize: 13.5,
              fontWeight: active ? 700 : 500,
              background: active ? "var(--surface-card)" : "transparent",
              boxShadow: active ? "var(--shadow-xs)" : "none",
              color: active ? "var(--text-strong)" : "var(--text-muted)",
            }}
          >
            {label}
          </a>
        );
      })}
    </nav>
  );
}

/**
 * Umbenennen — **nur für ein Gerät, das sich nicht selbst benennt.**
 *
 * Beim Koppeln entsteht der Name aus dem Wort „Gerät" und der Seriennummer, weil
 * ein Name da sein muss. Meldet das Gerät danach ein `settings.deviceName`,
 * gewinnt dieses (HM-041): Ein Gerät heißt in HausMeter, in seiner eigenen
 * Oberfläche und in Home Assistant dasselbe. Für ein Fremdgerät und für die
 * manuelle Eingabe bleibt dieser Dialog die einzige Quelle des Namens.
 */
function RenameDialog({
  device,
  open,
  onClose,
  onError,
}: {
  device: Device;
  open: boolean;
  onClose: () => void;
  onError: (message: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(device.name);

  const rename = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.PATCH("/devices/{deviceId}", {
        params: { path: { deviceId: device.id } },
        body: { name: name.trim() },
      });
      if (error || !data) throw new Error(apiErrorMessage(error) ?? "Umbenennen fehlgeschlagen.");
      return data;
    },
    onSuccess: async () => {
      onError(null);
      onClose();
      // Der Präfix trifft die Detailabfrage **und** die Liste — in der
      // Geräteverwaltung stünde sonst weiter der alte Name.
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
    },
    onError: (cause: Error) => onError(cause.message),
  });

  const tooShort = name.trim().length === 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Gerät umbenennen"
      description="Der Name steht in der Geräteverwaltung und überall dort, wo dieses Gerät genannt wird."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button type="submit" form={RENAME_FORM_ID} disabled={rename.isPending || tooShort}>
            Namen ändern
          </Button>
        </>
      }
    >
      <form
        id={RENAME_FORM_ID}
        onSubmit={(event) => {
          event.preventDefault();
          rename.mutate();
        }}
      >
        <Input
          label="Name"
          value={name}
          required
          maxLength={120}
          placeholder={`${device.deviceType} ${device.serialNumber}`}
          onChange={(event) => setName(event.target.value)}
        />
      </form>
    </Dialog>
  );
}

const RENAME_FORM_ID = "device-rename";

/**
 * Was dieses Gerät anbietet: seine **Datenpunkte** — je Kanal, mit dem zuletzt
 * eingegangenen Wert (ADR 0020, HM-049).
 *
 * **Ein Abschnitt je Kanal, und das ist der Unterschied zur flachen Liste.** Ein
 * CTS-Lesekopf mit Zusatzschnittstelle meldet an beiden Kanälen gleichlautende
 * Datenpunkte — „Bezug", „Einspeisung", „Wirkleistung" gibt es zweimal.
 * Untereinander ließen sie sich nur am Schlüssel unterscheiden; die Frage vor
 * der Seite lautet aber „was misst der zweite Zähler".
 *
 * **Gruppiert wird über `channelId`, nie über den Präfix im Schlüssel.** Dass
 * `ch0.energy-import` so aussieht, ist Lesbarkeit für Menschen; wer ihn zerlegt,
 * baut die Kanal-Ebene in den Adresspfad zurück, die HM-044 herausgenommen hat.
 * Die Rechnung steht in `lib/datapoint-groups.ts` und ist dort geprüft.
 *
 * **Und jetzt steht rechts ein Wert.** Er kommt aus der Rohschicht — der letzte,
 * der *angekommen* ist, nicht ein Abruf beim Gerät. Ein Datenpunkt ohne Wert
 * bleibt rechts leer; eine „0,0 kW" wäre eine Messung, die niemand vorgenommen
 * hat (Regel 8).
 */
function DatapointsSection({ device, siteId }: { device: Device; siteId: string }) {
  const datapoints = useDatapoints(device.id);
  const values = useDatapointValues(device.id);

  const items = datapoints.data?.items ?? [];
  const groups = groupDatapoints(device, items, values.data?.items ?? []);

  /**
   * Ob überhaupt schon ein Wert vorliegt. Solange keiner da ist, bekommt keine
   * Zeile ein „noch kein Wert" — an einem frisch gekoppelten Gerät stünde das
   * fünfzehnmal untereinander und sähe aus wie ein Schaden. Es ist der
   * Normalfall, und dafür genügt **ein** Satz unter der Liste.
   */
  const anyValue = groups.some((group) => group.latestAt !== null);

  return (
    <>
      {datapoints.isPending && <Card padding="sm">Wird geladen …</Card>}

      {!datapoints.isPending && items.length === 0 && (
        <Card>
          <EmptyState title="Keine Werte" icon={<icons.IconGauge size={30} />}>
            Dieses Gerät hat noch nichts gemeldet, was sich adressieren ließe.{" "}
            {device.gatewayDeviceId === null
              ? "Sobald es sich verbindet, trägt es seinen Katalog selbst ein."
              : "Das Gateway meldet den Katalog, sobald es das Gerät liest."}
          </EmptyState>
        </Card>
      )}

      {items.length > 0 &&
        groups.map((group) => (
          <DatapointGroupCard
            key={group.channelId ?? "ohne-kanal"}
            group={group}
            deviceId={device.id}
            siteId={siteId}
            showMissingNote={anyValue}
            controlledElsewhere={isEvccLoadpointDevice(device.deviceType)}
          />
        ))}

      {items.length > 0 && (
        <p
          style={{
            margin: 0,
            maxWidth: 640,
            fontSize: 12.5,
            lineHeight: 1.6,
            color: "var(--text-muted)",
          }}
        >
          {anyValue ? (
            <>
              Die Werte stammen aus der letzten Meldung des Geräts und werden jede Minute
              aktualisiert.
            </>
          ) : (
            <>
              <strong>Von diesem Gerät ist noch kein Wert eingegangen.</strong> Die Liste zeigt,
              was es messen kann.
            </>
          )}{" "}
          Ein Klick auf einen Wert zeigt seinen Verlauf.
        </p>
      )}
    </>
  );
}

/**
 * Ein Kanal mit seinen Datenpunkten — oder, an einem Gerät ohne Kanäle, die
 * einzige Liste ohne Überschrift.
 */
/** So viele Werte stehen je Kanal sichtbar da, der Rest hinter „Alle … anzeigen". */
const DATAPOINTS_VISIBLE = 8;

function DatapointGroupCard({
  group,
  deviceId,
  siteId,
  showMissingNote,
  controlledElsewhere,
}: {
  group: DatapointGroup;
  deviceId: string;
  siteId: string;
  showMissingNote: boolean;
  /** Das Gerät hat einen eigenen Reiter „Steuerung" — dort wird gesetzt, nicht hier. */
  controlledElsewhere: boolean;
}) {
  const count = group.entries.length;
  /* Eine Wallbox meldet rund neunzig Werte (UI-Durchsicht U6): Die Liste zeigt die
     ersten in ihrer Ordnung — Zählerstand, Leistung, Strom, Spannung — und klappt
     den Rest ein. Wer alle sehen will, öffnet sie. */
  const [showAll, setShowAll] = useState(false);
  const shown = showAll || count <= DATAPOINTS_VISIBLE + 2 ? group.entries : group.entries.slice(0, DATAPOINTS_VISIBLE);

  const card = (
    <Card padding="none">
      {/* Die Kopfzeile des Kits: Schlüssel, Anzahl, Stand. Sie steht nur da, wo
          es einen Kanal gibt — an einem kanallosen Gerät benennt sie nichts. */}
      {(group.channelKey !== null || group.latestAt !== null) && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--sp-3)",
            alignItems: "center",
            padding: "11px var(--sp-4)",
            background: "var(--surface-inset)",
            borderBottom: "1px solid var(--border-subtle)",
            fontSize: 12.5,
            color: "var(--text-muted)",
          }}
        >
          {/* Der Kanalschlüssel (`ch0`) nur beim Überfahren (ADR 0053) — die Überschrift
              darüber nennt den Kanal schon beim Namen. */}
          <span style={{ minWidth: 0 }} title={group.channelKey ?? undefined}>
            {count === 1 ? "1 Wert" : `${count} Werte`}
            {group.latestAt !== null && ` · Stand ${formatTimeAgo(group.latestAt)}`}
          </span>
          <span style={{ flex: 1 }} />
          {group.disabled && <Badge tone="warning">am Gerät abgeschaltet</Badge>}
        </div>
      )}

      {count === 0 && (
        <div
          style={{
            padding: "var(--sp-4)",
            fontSize: 12.5,
            lineHeight: 1.6,
            color: "var(--text-muted)",
          }}
        >
          Dieser Kanal meldet keine Werte. An einem Lesekopf mit unbestückter
          Zusatzschnittstelle ist das der Normalfall.
        </div>
      )}

      {shown.map((entry, index) => (
        <DatapointRow
          key={entry.point.id}
          entry={entry}
          deviceId={deviceId}
          siteId={siteId}
          showMissingNote={showMissingNote}
          writableHere={!controlledElsewhere}
          last={index === shown.length - 1 && shown.length === count}
        />
      ))}
      {shown.length < count && (
        <div style={{ padding: "var(--sp-2) var(--sp-4)", borderTop: "1px solid var(--border-subtle)" }}>
          <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
            Alle {count} Werte anzeigen
          </Button>
        </div>
      )}
    </Card>
  );

  return group.title === null ? (
    <Section title="Werte" subtitle="Was dieses Gerät misst und schaltet.">
      {card}
    </Section>
  ) : (
    <Section title={group.title}>{card}</Section>
  );
}

/**
 * Eine Zeile: links, was der Punkt ist — rechts, was zuletzt ankam.
 *
 * **Der Name führt zum Verlauf** (HM-053) und ist dafür ein echter `<a href>`,
 * wie in der Geräteliste: Ein `<div onClick>` ist mit der Tastatur nicht
 * erreichbar, für eine Sprachausgabe stumm und lässt sich nicht im neuen Tab
 * öffnen. Die Zeile folgt dem Link für die Maus und bekommt deshalb **keinen**
 * eigenen Tabulatorstopp, sonst liefe die Tastatur jede Zeile zweimal durch.
 *
 * **Ein Textdatenpunkt bekommt keinen.** Er hat keine Messreihe (HM-052); sein
 * Wert steht rechts in der Zeile, und ein Link führte auf eine Seite, die
 * dasselbe noch einmal sagt.
 */
function DatapointRow({
  entry,
  deviceId,
  siteId,
  showMissingNote,
  writableHere,
  last,
}: {
  entry: DatapointEntry;
  deviceId: string;
  siteId: string;
  showMissingNote: boolean;
  writableHere: boolean;
  last: boolean;
}) {
  const { point, value } = entry;
  const linked = point.valueType !== "string";
  /* „Setzen …" nur, wo das Gerät Schreiben gemeldet hat (HM-110, Regel 10) — und
     nicht an einem Gerät mit eigenem Reiter „Steuerung" (U6): Dort steht dieselbe
     Handlung in Worten, hier stünde sie an jeder zweiten von neunzig Zeilen. */
  const writable = writableHere && canWriteDatapoint(point);
  const route: Route = {
    kind: "datapoint",
    siteId,
    deviceId,
    datapointId: point.id,
    range: "24h",
  };

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: [
          "1fr",
          ...(writable ? ["auto"] : []),
          "auto",
          ...(linked ? ["auto"] : []),
        ].join(" "),
        gap: "var(--sp-4)",
        alignItems: "center",
        padding: "12px var(--sp-4)",
        borderBottom: last ? "none" : "1px solid var(--border-subtle)",
        cursor: linked ? "pointer" : "default",
      }}
      /* Nur Mausbequemlichkeit — der bedienbare Teil ist der Link im Namen. */
      onClick={linked ? () => navigate(route) : undefined}
      onMouseEnter={(event) => {
        if (linked) event.currentTarget.style.background = "var(--surface-inset)";
      }}
      onMouseLeave={(event) => {
        if (linked) event.currentTarget.style.background = "transparent";
      }}
    >
      <div style={{ minWidth: 0 }} title={point.key}>
        <div style={{ fontSize: 13.5, fontWeight: 600, color: "var(--text-strong)" }}>
          {linked ? (
            <a
              href={routePath(route)}
              /* Strg-, Cmd- und Mittelklick gehen an den Browser weiter und
                 öffnen einen neuen Tab. Ohne diese Prüfung wäre das `href` nur
                 Zierde. */
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                event.preventDefault();
                navigate(route);
              }}
              style={{ color: "inherit", textDecoration: "none" }}
            >
              {point.name}
            </a>
          ) : (
            point.name
          )}
        </div>
        {/* Der technische Schlüssel nur beim Überfahren (ADR 0053); sichtbar bleibt,
            was der Nutzer wissen muss — schaltbar, abgeschaltet. */}
        {(point.access !== "read" || !point.enabled) && (
          <div style={{ fontSize: 12, color: "var(--text-faint)" }}>
            {[point.access !== "read" ? DATAPOINT_ACCESS_LABELS[point.access] : null, point.enabled ? null : "abgeschaltet"]
              .filter(Boolean)
              .join(" · ")}
          </div>
        )}
      </div>

      {writable && (
        <DatapointWriteButton deviceId={deviceId} point={point} current={value?.value ?? null} />
      )}

      <div style={{ flexShrink: 0, textAlign: "right" }}>
        {point.valueType === "string" ? (
          /**
           * Ein **Textdatenpunkt** trägt seinen Wert am Katalogeintrag (HM-052):
           * Hersteller, Geräte-ID des Zählers. Er kommt nicht aus der Rohschicht,
           * und deshalb steht hier weder „noch kein Wert" noch ein Alter — die
           * Angabe ändert sich über die Lebensdauer eines Zählers nicht.
           *
           * Leichter gesetzt als eine Messung: Wer die Liste überfliegt, sucht
           * Zahlen, und eine fett gesetzte Zeichenkette zwischen ihnen läse sich
           * wie der wichtigste Wert des Geräts.
           */
          <span
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12.5,
              color: "var(--text-muted)",
              wordBreak: "break-all",
            }}
          >
            {point.textValue}
          </span>
        ) : value === null ? (
          /* Nur wenn dieses Gerät sonst liefert, ist das eine Aussage über
             diesen einen Datenpunkt. Liefert es gar nichts, steht der Satz
             einmal unter der Liste statt an jeder Zeile. */
          showMissingNote && (
            <span style={{ fontSize: 12, color: "var(--text-faint)" }}>noch kein Wert</span>
          )
        ) : (
          <>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontVariantNumeric: "tabular-nums",
                fontSize: 15,
                fontWeight: 700,
                color: "var(--text-strong)",
              }}
            >
              {formatDatapointValue(value.value, point)}
            </span>
            {/* Der Zeitpunkt steht nur an dem Wert, der gegenüber den anderen
                stehengeblieben ist — sonst wäre er in jeder Zeile dieselbe
                Auskunft, die schon in der Kopfzeile steht. */}
            {entry.stale && (
              <div style={{ fontSize: 11.5, color: "var(--text-faint)" }}>
                {formatTimeAgo(value.measuredAt)}
              </div>
            )}
          </>
        )}
      </div>

      {/* Derselbe Chevron wie am Ende einer Gerätezeile: Er sagt, dass es hinter
          dieser Zeile weitergeht. */}
      {linked && (
        <span style={{ display: "inline-flex", color: "var(--text-faint)", flexShrink: 0 }}>
          <icons.IconChevronRight size={16} />
        </span>
      )}
    </div>
  );
}

/**
 * Alles, was der Server über dieses Gerät weiß — als Karten, die nur erscheinen,
 * wenn sie etwas zu sagen haben.
 *
 * **Die Reihenfolge ist die Reihenfolge der Fragen**, von der Zugehörigkeit über
 * die Beschaffenheit zum Betrieb:
 *
 * | Karte                   | beantwortet                                    |
 * | ----------------------- | ---------------------------------------------- |
 * | Zuordnung               | Wohin gehört es?                               |
 * | Hardware und Firmware   | Was für ein Gerät ist es, welcher Stand läuft?  |
 * | Verbindung              | Wie hängt es im Netz — und warum vielleicht nicht? |
 * | Betrieb                 | Läuft es stabil?                               |
 * | Zähler am Gerät         | Lesen die Köpfe etwas?                         |
 *
 * **Nur „Zuordnung" ist immer da** — sie steht in der Datenbank und nicht in
 * einer Meldung. Alle übrigen entstehen aus dem, was das Gerät geschickt hat;
 * bleibt davon nichts, sagt das ein Satz statt vier leerer Kästen.
 *
 * Das Raster ist `auto-fit`: Fällt eine Karte weg, rücken die übrigen nach,
 * und die letzte allein stehende füllt die Breite, weil ein leerer Track
 * kollabiert. Es gibt deshalb keine Stelle, an der die Anordnung von Hand
 * nachgezogen werden müsste, wenn ein Gerät ein Feld mehr oder weniger meldet.
 */
function ReportSections({
  device,
  kind,
  siteName,
  sourceName,
  gateway,
  node,
}: {
  device: Device;
  kind: DeviceClass;
  siteName: string;
  sourceName: string | null;
  gateway: Device | undefined;
  node: GatewayNode | undefined;
}) {
  const candidates: readonly ReportSection[] = [
    /**
     * **Ein Gerät hinter dem Gateway meldet nichts über sich** (HM-113) — keine
     * Firmware, kein WLAN, keine Laufzeit; es hat keine Verbindung, über die es
     * das könnte. Was es an deren Stelle gibt, ist die Auskunft des Gateways:
     * wie es das Gerät liest und wo es steht.
     */
    ...(kind === "gateway_node"
      ? [
          {
            title: "Am Gateway",
            subtitle: "Wie das Gateway dieses Gerät findet und liest.",
            rows: nodeRows(device, node),
          },
        ]
      : []),
    {
      title: "Hardware und Firmware",
      subtitle: "Was für ein Gerät das ist.",
      rows: hardwareRows(device, device.diagnostics),
    },
    {
      title: "Verbindung",
      subtitle: snapshotSubtitle(device.diagnosticsAt, "Wie das Gerät im Netz hängt."),
      rows: networkRows(device, device.diagnostics),
    },
    {
      title: "Betrieb",
      subtitle: snapshotSubtitle(device.diagnosticsAt, "Wie lange und wie stabil es läuft."),
      rows: systemRows(device.diagnostics, {
        seconds: device.uptimeSeconds,
        at: device.uptimeAt,
      }),
    },
    {
      title: "Zähler am Gerät",
      subtitle: snapshotSubtitle(device.diagnosticsAt, "Was die Leseköpfe empfangen."),
      rows: meterRows(device.diagnostics),
    },
    /* Die Konfiguration steht seit HM-040 **nicht** mehr hier, sondern als
       eigener Reiter mit Eingabefeldern: Eine Liste, die zeigt, was man ändern
       könnte, und es nicht anbietet, ist derselbe Fehler wie ein Feld, das
       nichts absendet — nur mit umgekehrtem Vorzeichen. */
  ];

  const reported = candidates.filter(
    (section) => section.rows.length > 0 || section.footer !== undefined,
  );

  /* Sichtbar bleibt, wohin das Gerät gehört (und am Gateway, wie es gelesen wird);
     Hardware, Netz, Betrieb und Leseköpfe klappen ein (UI-Durchsicht U7). Die Seite
     war 2800 Pixel lang, und wer den Namen ändern oder neu starten will, sucht
     weder die MAC-Adresse noch den freien Speicher. */
  const visible = reported.filter((section) => section.title === "Am Gateway");
  const technical = reported.filter((section) => section.title !== "Am Gateway");
  const grid = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))",
    gap: "var(--sp-4)",
    alignItems: "start",
  } as const;

  return (
    <>
      <div style={grid}>
        <ReportCard
          title="Zuordnung"
          subtitle="Wohin dieses Gerät gehört."
          rows={assignmentRows(device, siteName, sourceName, gateway)}
        />
        {visible.map((section) => (
          <ReportCard key={section.title} {...section} />
        ))}
      </div>
      {technical.length > 0 && (
        <details>
          <summary
            style={{
              cursor: "pointer",
              fontSize: 15,
              fontWeight: 600,
              color: "var(--text-strong)",
              padding: "var(--sp-2) 0",
            }}
          >
            Technische Angaben
            <span style={{ fontSize: 12.5, fontWeight: 400, color: "var(--text-muted)", marginLeft: 8 }}>
              {technical.map((section) => section.title).join(" · ")}
            </span>
          </summary>
          <div style={{ ...grid, marginTop: "var(--sp-3)" }}>
            {technical.map((section) => (
              <ReportCard key={section.title} {...section} />
            ))}
          </div>
        </details>
      )}

      {/* Ein Gerät hinter dem Gateway meldet über sich nie etwas — der Satz „hat
          sich noch nicht gemeldet" wäre dort ein Dauerzustand und kein Hinweis. */}
      {reported.length === 0 && kind !== "gateway_node" && (
        <NothingReportedNotice device={device} kind={kind} />
      )}
    </>
  );
}

type ReportSection = {
  readonly title: string;
  readonly subtitle: string;
  readonly rows: readonly Row[];
  readonly footer?: ReactNode;
};

/** Abschnittskopf, Karte, Wertetabelle — für jede Auskunft dieselbe Form. */
function ReportCard({ title, subtitle, rows, footer }: ReportSection) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: "var(--sp-3)" }}>
      <SectionHeading title={title} subtitle={subtitle} />
      <Card>
        {/* Ohne die Prüfung stünde ein leeres `<dl>` da — unsichtbar, aber für
            eine Sprachausgabe eine angekündigte Liste ohne Inhalt. */}
        {rows.length > 0 && <DefinitionList rows={rows} />}
        {footer !== undefined && (
          <div style={{ marginTop: rows.length > 0 ? "var(--sp-4)" : 0 }}>{footer}</div>
        )}
      </Card>
    </section>
  );
}

/**
 * Wohin das Gerät gehört.
 *
 * **Die einzige Karte ohne Meldung des Geräts** — sie steht in der Datenbank und
 * ist deshalb immer vollständig. Ort im Gebäude, Kanalgruppe und Messintervall
 * aus dem Kit fehlen: alles drei Felder, die es nicht gibt. Geblieben ist, was
 * die Zugehörigkeit wirklich ausmacht.
 *
 * **Die Betriebsart steht nicht mehr darin.** Sie sagt, ob eine App ihre Werte
 * aus der Cloud oder direkt vom Gerät im LAN holt — eine Frage, die sich in
 * dieser Anwendung nicht stellt, denn sie ist die Cloud. Was der eine Fall
 * bedeutet, in dem es hier etwas ändert, steht in der Zeile darüber („ohne — nur
 * lokal") und als Hinweis über den Reitern.
 */
function assignmentRows(
  device: Device,
  siteName: string,
  sourceName: string | null,
  gateway: Device | undefined,
): readonly Row[] {
  return [
    ["Anlage", siteName],
    /* Der Weg herein (HM-113). Solange das Gateway lädt, fehlt die Zeile —
       eine Kennung an seiner Stelle wäre für niemanden lesbar. */
    ...(device.gatewayDeviceId !== null && gateway !== undefined
      ? [["Über Gateway", gateway.name] as const]
      : []),
    /* „ohne" ist ein echter Zustand: Ein Gerät im Betriebsmodus `local_only`
       meldet sich nirgends und hängt deshalb an keiner Integration. */
    [
      "Datenquelle",
      device.integrationInstanceId === null ? "ohne — nur lokal" : (sourceName ?? "unbekannt"),
    ],
    ["Zustand", CLAIM_STATUS_LABELS[device.claimStatus]],
    ["Verbunden seit", formatMonthYear(device.createdAt)],
  ];
}

/**
 * Der Zeitpunkt einer Momentaufnahme gehört in die Unterzeile.
 *
 * Eine Diagnose ohne Alter ist nichts wert: „WLAN: Heimnetz" von vor drei Wochen
 * beantwortet die Frage, warum gerade nichts ankommt, gerade nicht.
 */
function snapshotSubtitle(at: string | null, fallback: string): string {
  return at ? `Momentaufnahme von ${formatTimeAgo(at)}.` : fallback;
}

/**
 * Was der Server über dieses Gerät noch nicht weiß — **ein** Satz statt vier
 * leerer Kästen.
 *
 * Bei jedem Gerät, das noch nie verbunden war, ist das der Normalfall, und ein
 * Normalfall darf nicht wie ein Schaden aussehen. Deshalb steht hier auch, was
 * genau kommen wird: Wer weiß, dass die Firmware sich beim Verbinden vorstellt,
 * wartet, statt zu suchen.
 */
function NothingReportedNotice({ device, kind }: { device: Device; kind: DeviceClass }) {
  return (
    <Card>
      <EmptyState
        title="Das Gerät hat sich noch nicht gemeldet"
        icon={<icons.IconActivity size={30} />}
      >
        {kind === "gateway"
          ? "Beim Verbinden meldet das Gateway seine Fassung, seine Einstellungen und seine Laufzeit. "
          : "Beim Verbinden meldet es seine Firmware, seine Einstellungen und eine Diagnose — WLAN, Laufzeit und den Zustand jedes Zählers. "}
        Bis dahin steht hier nichts:{" "}
        {device.reachability === "reachable"
          ? "es ist erreichbar, hat aber noch nichts über sich gesagt."
          : "eine Voreinstellung anzuzeigen hieße, sie zu behaupten."}
      </EmptyState>
    </Card>
  );
}

/**
 * Der rot umrandete Kasten des Kits — mit der Handlung, die es hier wirklich
 * gibt.
 *
 * Zwei Sätze stehen darin, und beide sind nötig: **was verschwindet** (die
 * Rohmesswerte des Geräts, seine Zugangsdaten) und **was bleibt** (die
 * Zählerhistorie). Ohne den zweiten Satz klingt „freigeben" nach „Verbrauch
 * weg", und dann klickt niemand — auch nicht, wenn er das Gerät gerade verkauft
 * hat.
 */
function ReleaseSection({
  device,
  kind,
  onReleased,
  onError,
}: {
  device: Device;
  kind: DeviceClass;
  onReleased: () => void;
  onError: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const release = useMutation({
    mutationFn: async () => {
      const { error } = await api.DELETE("/devices/{deviceId}", {
        params: { path: { deviceId: device.id } },
      });
      if (error) throw new Error(apiErrorMessage(error) ?? "Freigeben fehlgeschlagen.");
    },
    onSuccess: async () => {
      // Erst die Listen erneuern, dann zurück: Sonst zeigt die
      // Geräteverwaltung für einen Augenblick das eben freigegebene Gerät.
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
      await queryClient.invalidateQueries({ queryKey: ["integrations"] });
      onReleased();
    },
    onError: (cause: Error) => onError(cause.message),
  });

  return (
    <Section title="Gerät verwalten" subtitle="Was sich hier tun lässt.">
      <Card>
        {/**
         * Der Weg nach einem Werksreset steht **vor** dem roten Kasten. Wer
         * hierher kommt, weil sein Gerät nichts mehr sendet, soll zuerst lesen,
         * dass Freigeben dafür nicht nötig ist.
         */}
        <p
          style={{
            margin: "0 0 var(--sp-4)",
            fontSize: 12.5,
            lineHeight: 1.6,
            color: "var(--text-muted)",
          }}
        >
          {kind === "gateway"
            ? "Sendet das Gateway nicht mehr, kopple es einfach erneut: Nach einer Neuinstallation zeigt es wieder einen Kopplungscode, und der bestätigt es diesem Konto ein zweites Mal."
            : "Sendet das Gerät nicht mehr, kopple es einfach erneut: Es zeigt nach einem Werksreset wieder einen Kopplungscode, und der bestätigt es diesem Konto ein zweites Mal."}{" "}
          Freigeben ist dafür <strong>nicht</strong> nötig —{" "}
          {kind === "gateway"
            ? "Kennung, zugelassene Geräte und Verlauf bleiben dabei erhalten."
            : "Kennung, Kanäle und Verlauf bleiben dabei erhalten."}
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--sp-3)",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "var(--sp-3)",
            border: "1px solid var(--hm-danger)",
            borderRadius: "var(--radius-md)",
          }}
        >
          <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: "1 1 260px" }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--hm-danger)" }}>
              Gerät freigeben
            </span>
            <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
              Das Gerät verlässt diese Anlage, seine Zugangsdaten werden widerrufen, und es kommt
              beim nächsten Verbindungsversuch nicht mehr herein. Seine einzelnen Meldungen laufen wie alle
              nach {RAW_RETENTION_DAYS} Tagen aus — die <strong>Zählerhistorie bleibt</strong>, denn der Verbrauch gehört
              dem Zähler und nicht dem Gerät. Danach kann jemand anderes es beanspruchen.
            </span>
            {/**
             * **Ein Gateway nimmt mehr mit als sich selbst** (HM-109). Seine Knoten
             * haben keine eigenen Zugangsdaten; was sie sendeberechtigt machte, war
             * seine Verbindung. Wer das nicht liest, gibt ein Gerät frei und wundert
             * sich, warum vier andere still werden.
             */}
            {kind === "gateway" && (
              <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
                Die Geräte <strong>hinter</strong> diesem Gateway liefern danach nichts mehr — sie
                haben keine eigene Verbindung. Sie bleiben mit ihrer Zählerhistorie stehen und
                liefern wieder, sobald hier ein Gateway verbunden und der jeweilige Knoten
                zugelassen ist.
              </span>
            )}
          </span>

          <span style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
            {confirming ? (
              <>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={release.isPending}
                  onClick={() => release.mutate()}
                >
                  {device.name} wirklich freigeben
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                  Abbrechen
                </Button>
              </>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => setConfirming(true)}>
                Freigeben …
              </Button>
            )}
          </span>
        </div>
      </Card>
    </Section>
  );
}

/**
 * Wie ein Gerät hinter dem Gateway die Anlage verlässt: **Ablehnen am Gateway**
 * (HM-113).
 *
 * Es hat keine Zugangsdaten, die sich widerrufen ließen — die API weist ein
 * Freigeben mit `409 device.gateway_node` ab. Abgelehnt wird dieselbe Zeile,
 * die auf der Seite des Gateways steht (`GatewaysService.dismiss`): Gerät,
 * Zeile und Zulassungsliste ändern sich zusammen, und dort lässt sich das
 * Gerät auch wieder zulassen.
 *
 * **Wieder zwei Sätze, und beide sind nötig** — was endet (die Werte kommen
 * nicht mehr an) und was bleibt (Zählerhistorie, Kennung, Quellbindung). Ohne
 * den zweiten klingt „ablehnen" endgültiger, als es ist.
 */
function DismissNodeSection({
  device,
  gateway,
  node,
  nodesLoaded,
  siteId,
  onDismissed,
  onError,
}: {
  device: Device;
  gateway: Device | undefined;
  node: GatewayNode | undefined;
  nodesLoaded: boolean;
  siteId: string;
  onDismissed: () => void;
  onError: (message: string) => void;
}) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const gatewayId = device.gatewayDeviceId;

  const dismiss = useMutation({
    mutationFn: async () => {
      if (gatewayId === null || node === undefined) {
        throw new Error("Die Zeile dieses Geräts beim Gateway ist nicht bekannt.");
      }
      const { error } = await api.POST("/gateways/{gatewayId}/nodes/{nodeId}/dismiss", {
        params: { path: { gatewayId, nodeId: node.id } },
      });
      if (error) throw new Error(apiErrorMessage(error) ?? "Ablehnen fehlgeschlagen.");
    },
    onSuccess: async () => {
      // Dieselben drei wie in der Knotenzeile: Knoten, Geräte, Gateway.
      await queryClient.invalidateQueries({ queryKey: ["gateway-nodes"] });
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
      await queryClient.invalidateQueries({ queryKey: ["site-gateway"] });
      onDismissed();
    },
    onError: (cause: Error) => onError(cause.message),
  });

  const gatewayRoute: Route | null =
    gatewayId === null ? null : { kind: "device", siteId, deviceId: gatewayId, tab: "overview" };

  return (
    <Section title="Gerät verwalten" subtitle="Was sich hier tun lässt.">
      <Card>
        <p
          style={{
            margin: "0 0 var(--sp-4)",
            fontSize: 12.5,
            lineHeight: 1.6,
            color: "var(--text-muted)",
          }}
        >
          Dieses Gerät hat <strong>keine eigene Verbindung</strong> — es kommt über{" "}
          {gatewayRoute !== null ? (
            <a
              href={routePath(gatewayRoute)}
              onClick={(event) => {
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
                event.preventDefault();
                navigate(gatewayRoute);
              }}
              style={{ color: "var(--text-strong)", fontWeight: 600 }}
            >
              {gateway?.name ?? "das Gateway"}
            </a>
          ) : (
            "das Gateway"
          )}{" "}
          herein. Liefert es nichts mehr, liegt der Weg dorthin im Heimnetz: Stromversorgung und
          Adresse des Geräts, dann das Gateway. Koppeln lässt es sich nicht, und neu starten lässt
          es sich von hier aus nicht.
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "var(--sp-3)",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "var(--sp-3)",
            border: "1px solid var(--hm-danger)",
            borderRadius: "var(--radius-md)",
          }}
        >
          <span style={{ display: "flex", flexDirection: "column", gap: 2, flex: "1 1 260px" }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--hm-danger)" }}>
              Am Gateway ablehnen
            </span>
            <span style={{ fontSize: 12.5, lineHeight: 1.5, color: "var(--text-muted)" }}>
              Das Gateway streicht das Gerät von seiner Zulassungsliste, und was es danach noch
              meldet, wird nicht gespeichert. Die <strong>Zählerhistorie bleibt</strong>, und auf
              der Seite des Gateways lässt es sich jederzeit wieder zulassen — mit derselben Kennung
              und derselben Quelle am Zählwerk.
            </span>
          </span>

          <span style={{ display: "flex", gap: "var(--sp-2)", flexWrap: "wrap" }}>
            {/* Ohne die Zeile beim Gateway gibt es nichts abzulehnen. Solange sie
                lädt, wartet der Knopf; fehlt sie danach, sagt es der Satz. */}
            {nodesLoaded && node === undefined ? (
              <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                Beim Gateway ist dieses Gerät nicht mehr zugelassen.
              </span>
            ) : confirming ? (
              <>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={dismiss.isPending}
                  onClick={() => dismiss.mutate()}
                >
                  {device.name} wirklich ablehnen
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirming(false)}>
                  Abbrechen
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                disabled={node === undefined}
                onClick={() => setConfirming(true)}
              >
                Ablehnen …
              </Button>
            )}
          </span>
        </div>
      </Card>
    </Section>
  );
}

/**
 * Der Reiter „Steuerung" eines Ladepunkts — derselbe Zustand wie auf „Laden"
 * (`useChargePoint`), nur mit Strömen, Phasen und Aufträgen.
 */
function DeviceChargePointControl({ deviceId }: { deviceId: string }) {
  const point = useChargePoint(deviceId);
  if (point.isPending) return <Card padding="sm">Wird geladen …</Card>;
  if (point.isError || point.data === undefined) {
    return <Alert tone="danger">{point.error?.message ?? "Der Ladepunkt konnte nicht geladen werden."}</Alert>;
  }
  return <ChargePointControl point={point.data} />;
}

/**
 * Der Zustand eines Speichers (HM-122) — Ladezustand, Leistung mit Richtung,
 * geladene und entladene Energie. **Nur, was gemeldet wurde**: Ein Feld ohne
 * Datenpunkt bekommt keine Zeile (HM-041).
 */
function StorageSection({ deviceId }: { deviceId: string }) {
  const state = useStorageState(deviceId);
  if (state.isPending) return <Card padding="sm">Der Speicher wird geladen …</Card>;
  if (state.isError || state.data === undefined) {
    return <Alert tone="danger">{state.error?.message ?? "Der Speicher konnte nicht geladen werden."}</Alert>;
  }
  const s = state.data;
  const rows = storageRows(s);
  return (
    <Section
      title="Speicher"
      subtitle={
        s.measuredAt === null
          ? "Noch kein Wert gemeldet."
          : `Zuletzt gemeldet ${formatTimeAgo(s.measuredAt)}.`
      }
    >
      <Card>
        {rows.length === 0 ? (
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 13 }}>
            Der Speicher hat noch keinen seiner verabredeten Werte gemeldet.
          </p>
        ) : (
          <DefinitionList rows={rows} />
        )}
      </Card>
    </Section>
  );
}

const INVERTER_KIND_LABELS = {
  pv: "PV-Wechselrichter",
  hybrid: "Hybrid-Wechselrichter",
  battery: "Batteriewechselrichter",
} as const;

/**
 * Der Zustand eines Wechselrichters (HM-126, ADR 0039) — eine Karte je Teil,
 * den er meldet: PV-Eingang, Batterie, Netzseite. **Die Art steht im Titel und
 * kommt aus der API**, die sie aus dem Katalog liest; meldet er nur die
 * Netzseite, heißt er schlicht „Wechselrichter", statt zu raten, was dahinter
 * steht (Regel 9). Und **ein Satz sagt, was im Messkonzept gilt**: Netzseite
 * oder die Teile dahinter, nie beides — die API lehnt es ab, aber wer die Regel
 * erst am Fehler lernt, hat schon falsch eingerichtet.
 */
function InverterSection({ deviceId }: { deviceId: string }) {
  const state = useInverterState(deviceId);
  if (state.isPending) return <Card padding="sm">Der Wechselrichter wird geladen …</Card>;
  if (state.isError || state.data === undefined) {
    return (
      <Alert tone="danger">{state.error?.message ?? "Der Wechselrichter konnte nicht geladen werden."}</Alert>
    );
  }
  const s = state.data;

  const parts: { title: string; rows: (readonly [string, string])[] }[] = [];
  if (s.pv !== null) {
    const rows: (readonly [string, string])[] = [];
    if (s.pv.generationW !== null) rows.push(["Leistung", formatKw(s.pv.generationW)]);
    if (s.pv.energyWh.value !== null) rows.push(["Ertrag gesamt", formatKwh(s.pv.energyWh.value)]);
    parts.push({ title: "PV-Eingang", rows });
  }
  if (s.battery !== null) parts.push({ title: "Batterie", rows: storageRows(s.battery) });
  if (s.ac !== null) {
    const rows: (readonly [string, string])[] = [];
    const power = s.ac.powerW.value;
    if (power !== null) {
      rows.push([
        "Leistung",
        power < 0 ? `${formatKw(-power)} · gibt ab` : power > 0 ? `${formatKw(power)} · bezieht` : "0 kW",
      ]);
    }
    if (s.ac.energyExportWh.value !== null) {
      rows.push(["Abgegeben gesamt", formatKwh(s.ac.energyExportWh.value)]);
    }
    if (s.ac.energyImportWh.value !== null) {
      rows.push(["Bezogen gesamt", formatKwh(s.ac.energyImportWh.value)]);
    }
    parts.push({ title: "Netzseite", rows });
  }

  const behind = s.pv !== null || s.battery !== null;
  return (
    <Section
      title={s.kind === null ? "Wechselrichter" : INVERTER_KIND_LABELS[s.kind]}
      subtitle={
        s.measuredAt === null
          ? "Noch kein Wert gemeldet."
          : `Zuletzt gemeldet ${formatTimeAgo(s.measuredAt)}.`
      }
    >
      {parts.length === 0 ? (
        <Card>
          <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 13 }}>
            Der Wechselrichter hat noch keinen seiner verabredeten Werte gemeldet.
          </p>
        </Card>
      ) : (
        <div
          style={{
            display: "grid",
            gap: "var(--sp-3)",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
          }}
        >
          {parts.map((part) => (
            <Card key={part.title} title={part.title}>
              {part.rows.length === 0 ? (
                <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 13 }}>Noch kein Wert.</p>
              ) : (
                <DefinitionList rows={part.rows} />
              )}
            </Card>
          ))}
        </div>
      )}
      {s.ac !== null && behind && (
        <p style={{ margin: "var(--sp-3) 0 0", fontSize: 12.5, lineHeight: 1.6, color: "var(--text-muted)" }}>
          Die Netzseite zählt, was hinter ihr erzeugt, geladen und entladen wird. Im Messkonzept
          steht deshalb <strong>entweder</strong> die Netzseite <strong>oder</strong>{" "}
          {s.pv !== null && s.battery !== null
            ? "PV-Eingang und Batterie"
            : s.pv !== null
              ? "der PV-Eingang"
              : "die Batterie"}{" "}
          — beides zählte doppelt.
        </p>
      )}
    </Section>
  );
}
